import React from "react";
import { useActionItemsHeaderHoverMenu } from "./useActionItemsHeaderHoverMenu";

const SortIcon = () => (
  <span className="action-items-sort-hover-menu__icon" aria-hidden="true">
    ↑↓
  </span>
);

/**
 * Hover flyout for list sort on the action-items header.
 *
 * @example
 * // Input — non-default sort selected
 * <ActionItemsSortHoverMenu
 *   value="due_soonest"
 *   options={[{ value: "default", label: "Event date — recent first" }, ...]}
 *   onChange={setSort}
 *   sortActive
 * />
 * // Output — sort icon with active dot; hover shows radio list
 */
const ActionItemsSortHoverMenu = ({
  value,
  options = [],
  onChange,
  sortActive = false,
  menuLabel = "Sort action items",
}) => {
  const { menuOpen, openMenu, closeMenu, handleMouseLeave } =
    useActionItemsHeaderHoverMenu("sort");

  if (!options.length) return null;

  const toggleClass = [
    "action-items-sort-hover-menu__toggle",
    "action-items-header-icon-btn",
    sortActive ? "action-items-sort-hover-menu__toggle--active" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const ariaLabel = sortActive ? `${menuLabel} (sort active)` : menuLabel;

  const rootClass = [
    "action-items-header-hover-menu",
    "action-items-sort-hover-menu",
    menuOpen ? "action-items-header-hover-menu--open" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={rootClass}
      onMouseEnter={openMenu}
      onMouseLeave={handleMouseLeave}
      onFocus={openMenu}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          closeMenu();
        }
      }}
    >
      <button
        type="button"
        className={toggleClass}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-label={ariaLabel}
        title={ariaLabel}
      >
        <SortIcon />
      </button>
      <div className="action-items-header-hover-menu__panel" role="menu" aria-label="Sort by">
        <div className="action-items-header-hover-menu__label">Sort by</div>
        {options.map(({ value: optionValue, label }) => {
          const selected = value === optionValue;
          return (
            <button
              key={optionValue}
              type="button"
              className={`action-items-header-hover-menu__option${
                selected ? " action-items-header-hover-menu__option--selected" : ""
              }`}
              role="menuitemradio"
              aria-checked={selected}
              onClick={() => onChange?.(optionValue)}
            >
              <span>{label}</span>
              {selected ? (
                <span className="action-items-header-hover-menu__check" aria-hidden="true">
                  ✓
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ActionItemsSortHoverMenu;
